import React, { useState, useEffect } from 'react';
import api from '../api';
import { Save, Download, Check, Bell, Mail, Clock, Users, FileText, Zap, AlertTriangle, CloudLightning } from 'lucide-react';

// Email notifikacije trenutno nisu u funkciji (Gmail SMTP blokiran na Hetzneru) — sekcija sakrivena dok se ne riješi.
const EMAIL_SECTION_ENABLED = false;

// Nazivi moraju odgovarati cap:areaDesc u Meteoalarm feedu. Od 2026-09-22 DHMZ kopno
// izdaje po županijama; morska područja zadržala su stare engleske nazive.
const DHMZ_REGION_GROUPS = [
  { label: 'Županije', options: [
    'Grad Zagreb', 'Zagrebačka', 'Krapinsko-zagorska', 'Varaždinska', 'Međimurska',
    'Koprivničko-križevačka', 'Bjelovarsko-bilogorska', 'Virovitičko-podravska', 'Požeško-slavonska',
    'Brodsko-posavska', 'Osječko-baranjska', 'Vukovarsko-srijemska', 'Karlovačka', 'Sisačko-moslavačka',
    'Primorsko-goranska', 'Ličko-senjska', 'Istarska', 'Zadarska', 'Šibensko-kninska',
    'Splitsko-dalmatinska', 'Dubrovačko-neretvanska',
  ] },
  { label: 'Morska područja', options: [
    'West Istrian coast region', 'Kvarner i Kvarneric region', 'Velebit channel region',
    'North Dalmatia region', 'Middle Dalmatia region', 'South Dalmatia region',
  ] },
];
const DHMZ_REGION_LABELS = {
  'West Istrian coast region': 'Zapadna obala Istre',
  'Kvarner i Kvarneric region': 'Kvarner i Kvarnerić',
  'Velebit channel region': 'Velebitski kanal',
  'North Dalmatia region': 'Sjeverna Dalmacija',
  'Middle Dalmatia region': 'Srednja Dalmacija',
  'South Dalmatia region': 'Južna Dalmacija',
};

function ToggleSwitch({ checked, onChange, disabled }) {
  return (
    <div
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: 44, height: 24, borderRadius: 12, cursor: disabled ? 'not-allowed' : 'pointer',
        background: checked ? '#00b894' : '#dfe6e9',
        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div style={{
        position: 'absolute', top: 2, left: checked ? 22 : 2,
        width: 20, height: 20, borderRadius: '50%', background: 'white',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

function SettingRow({ label, description, settingKey, value, onChange }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '13px 0', borderBottom: '1px solid #eef0f3', gap: 16,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 16, fontWeight: 500, color: '#2d3436', marginBottom: 3 }}>{label}</div>
        {description && <div style={{ fontSize: 13, color: '#b2bec3', lineHeight: 1.5 }}>{description}</div>}
      </div>
      <ToggleSwitch
        checked={value === 'true'}
        onChange={v => onChange(settingKey, v ? 'true' : 'false')}
      />
    </div>
  );
}

function SubHeader({ label }) {
  return (
    <div style={{
      fontSize: 12, fontWeight: 600, color: '#b2bec3',
      padding: '14px 0 6px', textTransform: 'uppercase', letterSpacing: '0.6px',
      display: 'flex', alignItems: 'center', gap: 6,
    }}>
      {label}
    </div>
  );
}

function SectionCard({ title, icon, children }) {
  return (
    <div style={{
      background: 'white', borderRadius: 16, marginBottom: 12,
      border: '1px solid #eef0f3',
      borderLeft: '2px solid #1a2a4a',
      boxShadow: '0 2px 8px rgba(0,0,0,0.07)', overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 20px', background: '#f5f6fa',
        borderBottom: '1px solid #eef0f3',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{ color: '#1a2a4a', display: 'flex', alignItems: 'center' }}>{icon}</span>
        <span style={{ fontWeight: 700, fontSize: 17, color: '#1a2a4a' }}>{title}</span>
      </div>
      <div style={{ padding: '0 20px' }}>
        {children}
      </div>
    </div>
  );
}

function ExportSection({ allGroups, allCategories }) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const monthNames = ['Cijela godina', 'Siječanj', 'Veljača', 'Ožujak', 'Travanj', 'Svibanj', 'Lipanj', 'Srpanj', 'Kolovoz', 'Rujan', 'Listopad', 'Studeni', 'Prosinac'];

  const [taskFilters, setTaskFilters] = useState({
    year: currentYear, month: 0, status: 'all', group_id: '', category_id: '', format: 'txt'
  });
  const [interventionFilters, setInterventionFilters] = useState({
    year: currentYear, month: 0, format: 'txt'
  });
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (type) => {
    setDownloading(type);
    try {
      const filters = type === 'tasks' ? taskFilters : interventionFilters;
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v !== 0) params.append(k, v); });
      if (filters.month === 0) params.delete('month');
      const endpoint = type === 'tasks' ? '/api/export/tasks' : '/api/export/interventions';
      const res = await fetch(`${endpoint}?${params.toString()}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Greška pri preuzimanju.');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type === 'tasks' ? 'zadaci' : 'intervencije'}-${filters.year}.${filters.format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Greška pri preuzimanju izvještaja.');
    } finally {
      setDownloading(null);
    }
  };

  const selectStyle = {
    padding: '7px 10px', border: '1px solid #dfe6e9', borderRadius: 8,
    fontSize: 14, background: 'white', color: '#2d3436', outline: 'none',
  };

  const formatBtns = (filters, setFilters) => ['txt', 'docx', 'xlsx'].map(f => (
    <button
      key={f}
      onClick={() => setFilters(p => ({ ...p, format: f }))}
      style={{
        padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600,
        border: filters.format === f ? '2px solid #0984e3' : '2px solid #dfe6e9',
        background: filters.format === f ? '#e8f4fd' : '#f8f9fa',
        color: filters.format === f ? '#0984e3' : '#636e72',
        transition: 'all 0.15s',
      }}
    >{f.toUpperCase()}</button>
  ));

  return (
    <SectionCard title="Export izvještaja" icon={<FileText size={16} />}>
      <div style={{ paddingTop: 16, paddingBottom: 8 }}>
        {/* Zadaci */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#b2bec3', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>Zadaci</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <select style={selectStyle} value={taskFilters.year} onChange={e => setTaskFilters(p => ({ ...p, year: parseInt(e.target.value) }))}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select style={selectStyle} value={taskFilters.month} onChange={e => setTaskFilters(p => ({ ...p, month: parseInt(e.target.value) }))}>
              {monthNames.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
            <select style={selectStyle} value={taskFilters.status} onChange={e => setTaskFilters(p => ({ ...p, status: e.target.value }))}>
              <option value="all">Svi statusi</option>
              <option value="active">Aktivni</option>
              <option value="done">Završeni</option>
            </select>
            {allGroups.length > 0 && (
              <select style={selectStyle} value={taskFilters.group_id} onChange={e => setTaskFilters(p => ({ ...p, group_id: e.target.value }))}>
                <option value="">Sve grupe</option>
                {allGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            )}
            {allCategories.length > 0 && (
              <select style={selectStyle} value={taskFilters.category_id} onChange={e => setTaskFilters(p => ({ ...p, category_id: e.target.value }))}>
                <option value="">Sve kategorije</option>
                {allCategories.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
              </select>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6 }}>{formatBtns(taskFilters, setTaskFilters)}</div>
            <button
              className="btn btn-primary"
              style={{ marginLeft: 'auto', padding: '7px 16px', fontSize: 14, background: '#1a2a4a', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              onClick={() => handleDownload('tasks')}
              disabled={downloading === 'tasks'}
            >
              {downloading === 'tasks' ? 'Preuzimam...' : <><Download size={14} /> Preuzmi</>}
            </button>
          </div>
        </div>

        <div style={{ borderTop: '1px solid #f0f0f0', marginBottom: 20 }} />

        {/* Intervencije */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#b2bec3', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>Intervencije</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <select style={selectStyle} value={interventionFilters.year} onChange={e => setInterventionFilters(p => ({ ...p, year: parseInt(e.target.value) }))}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select style={selectStyle} value={interventionFilters.month} onChange={e => setInterventionFilters(p => ({ ...p, month: parseInt(e.target.value) }))}>
              {monthNames.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6 }}>{formatBtns(interventionFilters, setInterventionFilters)}</div>
            <button
              className="btn btn-primary"
              style={{ marginLeft: 'auto', padding: '7px 16px', fontSize: 14, background: '#1a2a4a', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              onClick={() => handleDownload('interventions')}
              disabled={downloading === 'interventions'}
            >
              {downloading === 'interventions' ? 'Preuzimam...' : <><Download size={14} /> Preuzmi</>}
            </button>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

const pushDescriptions = {
  push_task_removed: 'Šalje se korisniku kada ga admin ili vlasnik grupe ukloni sa zadatka.',
  push_new_task: 'Šalje se korisniku kada ga admin ili vlasnik grupe dodijeli na zadatak.',
  push_group_task: 'Šalje se svim članovima grupe kada se kreira novi zadatak za njihovu grupu, a nisu direktno dodijeljeni.',
  push_task_done: 'Šalje se svim dodijeljenim korisnicima kada svi označe rad kao završen; za ponavljajući zadatak šalje obavijest o novom zadatku.',
  push_task_closed: 'Šalje se svim dodijeljenim korisnicima kada admin ili vlasnik grupe ručno zatvori zadatak.',
  push_task_review: 'Šalje se korisniku koji je predložio zadatak kada admin odobri ili odbije prijedlog.',
  push_removal_request: 'Šalje se korisniku kada admin ili vlasnik grupe odluči o zahtjevu za odjavu sa zadatka.',
  push_volunteer_task: 'Šalje se adminu i vlasnicima grupe kada se korisnik prijavi kao zainteresiran za zadatak.',
  push_new_intervention: 'Šalje se svim korisnicima sustava kada se aktivira nova intervencija.',
  push_intervention_participant: 'Šalje se korisniku kada ga admin ili manager doda kao sudionika intervencije.',
  push_volunteer_intervention: 'Šalje se adminu i manageru kada se korisnik prijavi za sudjelovanje na intervenciji.',
  push_password_reset: 'Šalje se korisniku kada mu admin postavi novu lozinku.',
  push_notify_groups_allowed: 'U „Pošalji obavijest” (⋯ na zadatku) admin i vlasnik grupe mogu odabrati članove grupa zadatka. Obavijest ide u aplikaciju i kao push.',
  push_notify_assigned_allowed: 'U „Pošalji obavijest” (⋯ na zadatku) admin i vlasnik grupe mogu odabrati dodijeljene na zadatak. Obavijest ide u aplikaciju i kao push.',
};

const mailDescriptions = {
  mail_task_removed: 'Šalje se korisniku putem emaila kada ga admin ili vlasnik grupe ukloni sa zadatka.',
  mail_new_task: 'Šalje se korisniku putem emaila kada ga admin ili vlasnik grupe dodijeli na zadatak.',
  mail_group_task: 'Šalje se svim članovima grupe putem emaila kada se kreira novi zadatak za njihovu grupu, a nisu direktno dodijeljeni.',
  mail_task_done: 'Šalje se svim dodijeljenim korisnicima putem emaila kada svi označe rad kao završen; za ponavljajući zadatak šalje obavijest o novom zadatku.',
  mail_task_closed: 'Šalje se svim dodijeljenim korisnicima putem emaila kada admin ili vlasnik grupe ručno zatvori zadatak.',
  mail_task_review: 'Šalje se putem emaila korisniku koji je predložio zadatak kada admin odobri ili odbije prijedlog.',
  mail_removal_request: 'Šalje se putem emaila korisniku kada admin ili vlasnik grupe odluči o zahtjevu za odjavu sa zadatka.',
  mail_volunteer_task: 'Šalje se adminu i vlasnicima grupe putem emaila kada se korisnik prijavi kao zainteresiran za zadatak.',
  mail_new_intervention: 'Šalje se svim korisnicima sustava putem emaila kada se aktivira nova intervencija.',
  mail_intervention_participant: 'Šalje se korisniku putem emaila kada ga admin ili manager doda kao sudionika intervencije.',
  mail_volunteer_intervention: 'Šalje se adminu i manageru putem emaila kada se korisnik prijavi za sudjelovanje na intervenciji.',
  mail_reminder_days: 'Šalje se putem emaila svim dodijeljenim korisnicima X dana prije početka zadatka.',
  mail_reminder_hours: 'Šalje se putem emaila svim dodijeljenim korisnicima X sati prije početka zadatka.',
  mail_password_reset: 'Šalje se korisniku putem emaila kada mu admin postavi novu lozinku.',
};

function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [allGroups, setAllGroups] = useState([]);
  const [allCategories, setAllCategories] = useState([]);

  useEffect(() => {
    fetchSettings();
    api.get('/groups').then(res => setAllGroups(res.data)).catch(() => {});
    api.get('/categories').then(res => setAllCategories(res.data)).catch(() => {});
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings');
      setSettings(res.data);
    } catch (err) {
      setError('Greška pri dohvatu postavki.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  const handleNumberChange = (key, value) => {
    const num = parseInt(value);
    if (!isNaN(num) && num >= 0) setSettings(prev => ({ ...prev, [key]: String(num) }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put('/settings', settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError('Greška pri spremanju postavki.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Učitavanje...</div>;
  if (!settings) return <div style={{ color: '#d63031' }}>{error}</div>;

  const inputStyle = {
    width: 70, padding: '5px 8px', border: '1px solid #dfe6e9',
    borderRadius: 8, fontSize: 16, outline: 'none',
  };

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#1a2a4a' }}>Postavke</h2>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
          style={{ minWidth: 110, background: '#1a2a4a', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
        >
          {saving ? 'Spremam...' : saved ? <><Check size={14} /> Spremljeno!</> : <><Save size={14} /> Spremi</>}
        </button>
      </div>

      {error && <p className="error" style={{ marginBottom: 16 }}>{error}</p>}

      {/* Export */}
      <ExportSection allGroups={allGroups} allCategories={allCategories} />

      {/* Prikaz korisnika */}
      <SectionCard title="Prikaz broja korisnika na zadacima" icon={<Users size={16} />}>
        <div style={{ padding: '16px 0' }}>
          <div style={{ fontSize: 14, color: '#8395a7', marginBottom: 14, lineHeight: 1.5 }}>
            Odaberi kako se broje korisnici u oznaci (X/Y) pored naziva zadatka.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { value: 'assigned', label: 'Dodjeljeni', desc: 'Broji sve dodijeljene (pending + prihvatili), isključuje odbijene' },
              { value: 'accepted', label: 'Prihvatili', desc: 'Broji samo one koji su eksplicitno prihvatili' },
            ].map(opt => (
              <label key={opt.value} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                border: settings.task_count_mode === opt.value ? '2px solid #1a2a4a' : '2px solid #eef0f3',
                background: settings.task_count_mode === opt.value ? '#f0f3f8' : '#f8f9fa',
                transition: 'all 0.15s',
              }}>
                <input type="radio" name="task_count_mode" value={opt.value}
                  checked={settings.task_count_mode === opt.value}
                  onChange={() => handleChange('task_count_mode', opt.value)}
                  style={{ marginTop: 3 }} />
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#2d3436', marginBottom: 2 }}>{opt.label}</div>
                  <div style={{ fontSize: 13, color: '#8395a7' }}>{opt.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </SectionCard>

      {/* Podsjetnici */}
      <SectionCard title="Podsjetnici" icon={<Clock size={16} />}>
        <div style={{ paddingTop: 8 }}>
          <SettingRow
            label="Podsjetnik X dana prije"
            description="Šalje se jednom dnevno u definirani sat"
            settingKey="reminder_days_enabled"
            value={settings.reminder_days_enabled}
            onChange={handleChange}
          />
          {settings.reminder_days_enabled === 'true' && (
            <div style={{ padding: '8px 0 12px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 14, color: '#636e72' }}>Broj dana:</span>
              <input type="number" min="1" max="30" value={settings.reminder_days}
                onChange={e => handleNumberChange('reminder_days', e.target.value)} style={inputStyle} />
              <span style={{ fontSize: 14, color: '#636e72' }}>dana prije zadatka, u</span>
              <input type="number" min="0" max="23" value={settings.reminder_days_hour || '17'}
                onChange={e => handleNumberChange('reminder_days_hour', e.target.value)} style={{ ...inputStyle, width: 55 }} />
              <span style={{ fontSize: 14, color: '#636e72' }}>:00 h</span>
            </div>
          )}
          <SettingRow
            label="Podsjetnik X sati prije"
            description="Provjerava svakih 15 minuta, šalje kada je zadatak za X sati"
            settingKey="reminder_hours_enabled"
            value={settings.reminder_hours_enabled}
            onChange={handleChange}
          />
          {settings.reminder_hours_enabled === 'true' && (
            <div style={{ padding: '8px 0 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 14, color: '#636e72' }}>Broj sati:</span>
              <input type="number" min="1" max="48" value={settings.reminder_hours}
                onChange={e => handleNumberChange('reminder_hours', e.target.value)} style={inputStyle} />
              <span style={{ fontSize: 14, color: '#636e72' }}>sati prije zadatka</span>
            </div>
          )}
        </div>
      </SectionCard>

      {/* DHMZ Vremenska upozorenja */}
      <SectionCard title="Vremenska upozorenja (DHMZ)" icon={<CloudLightning size={16} />}>
        <div style={{ paddingTop: 8 }}>
          <SettingRow
            label="Prati upozorenja"
            description="Automatski provjerava DHMZ upozorenja svakih 15 minuta i šalje push svim korisnicima."
            settingKey="dhmz_enabled"
            value={settings.dhmz_enabled}
            onChange={handleChange}
          />
          {settings.dhmz_enabled === 'true' && (<>
            <SettingRow
              label="Push notifikacije"
              description="Šalje push na telefon uz upozorenje. Isključi ako želiš samo prikaz u aplikaciji."
              settingKey="dhmz_push_enabled"
              value={settings.dhmz_push_enabled ?? 'true'}
              onChange={handleChange}
            />
            {(settings.dhmz_push_enabled ?? 'true') === 'true' && (
              <div style={{ padding: '12px 0 8px', paddingLeft: 8, borderLeft: '2px solid #eef0f3', marginLeft: 4, marginBottom: 4 }}>
                <div style={{ fontSize: 14, color: '#636e72', marginBottom: 10 }}>Minimalna razina za push</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { value: 'Yellow', label: 'Sve razine', desc: 'Push za žuta, narančasta i crvena', color: '#f0c000' },
                    { value: 'Orange', label: 'Narančasta i crvena', desc: 'Push samo za ozbiljnija upozorenja', color: '#e17055' },
                    { value: 'Red', label: 'Samo crvena', desc: 'Push samo za najozbiljnija upozorenja', color: '#d63031' },
                  ].map(opt => (
                    <label key={opt.value} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                      border: (settings.dhmz_push_min_severity || 'Yellow') === opt.value ? `2px solid ${opt.color}` : '2px solid #eef0f3',
                      background: (settings.dhmz_push_min_severity || 'Yellow') === opt.value ? '#f8f9fa' : '#f8f9fa',
                      transition: 'all 0.15s',
                    }}>
                      <input type="radio" name="dhmz_push_min_severity" value={opt.value}
                        checked={(settings.dhmz_push_min_severity || 'Yellow') === opt.value}
                        onChange={() => handleChange('dhmz_push_min_severity', opt.value)}
                        style={{ marginTop: 3 }} />
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#2d3436', marginBottom: 1 }}>{opt.label}</div>
                        <div style={{ fontSize: 13, color: '#8395a7' }}>{opt.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div style={{ padding: '12px 0 4px' }}>
              <div style={{ fontSize: 14, color: '#636e72', marginBottom: 8 }}>Regija</div>
              <select
                value={settings.dhmz_region || 'Grad Zagreb'}
                onChange={e => handleChange('dhmz_region', e.target.value)}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #dfe6e9', borderRadius: 8, fontSize: 15, background: 'white', color: '#2d3436', outline: 'none' }}
              >
                {!DHMZ_REGION_GROUPS.some(g => g.options.includes(settings.dhmz_region || 'Grad Zagreb')) && (
                  <option value={settings.dhmz_region}>{settings.dhmz_region} (stari naziv — odaberi ponovno)</option>
                )}
                {DHMZ_REGION_GROUPS.map(g => (
                  <optgroup key={g.label} label={g.label}>
                    {g.options.map(v => <option key={v} value={v}>{DHMZ_REGION_LABELS[v] || v}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div style={{ padding: '12px 0 8px' }}>
              <div style={{ fontSize: 14, color: '#636e72', marginBottom: 10 }}>Minimalna razina upozorenja</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { value: 'Yellow', label: 'Sve razine', desc: 'Žuta + narančasta + crvena' },
                  { value: 'Orange', label: 'Narančasta i crvena', desc: 'Ignorira žuta upozorenja' },
                  { value: 'Red', label: 'Samo crvena', desc: 'Samo najozbiljnija upozorenja' },
                ].map(opt => (
                  <label key={opt.value} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                    border: (settings.dhmz_min_severity || 'Yellow') === opt.value ? '2px solid #1a2a4a' : '2px solid #eef0f3',
                    background: (settings.dhmz_min_severity || 'Yellow') === opt.value ? '#f0f3f8' : '#f8f9fa',
                    transition: 'all 0.15s',
                  }}>
                    <input type="radio" name="dhmz_min_severity" value={opt.value}
                      checked={(settings.dhmz_min_severity || 'Yellow') === opt.value}
                      onChange={() => handleChange('dhmz_min_severity', opt.value)}
                      style={{ marginTop: 3 }} />
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#2d3436', marginBottom: 1 }}>{opt.label}</div>
                      <div style={{ fontSize: 13, color: '#8395a7' }}>{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </>)}
        </div>
      </SectionCard>

      {/* Push notifikacije */}
      <SectionCard title="Push notifikacije" icon={<Bell size={16} />}>
        <div style={{ paddingTop: 8 }}>
          <SubHeader label="Zadaci" />
          <SettingRow label="Dodjela na zadatak" description={pushDescriptions.push_new_task} settingKey="push_new_task" value={settings.push_new_task} onChange={handleChange} />
          <SettingRow label="Uklonjen sa zadatka" description={pushDescriptions.push_task_removed} settingKey="push_task_removed" value={settings.push_task_removed} onChange={handleChange} />
          <SettingRow label="Novi grupni zadatak" description={pushDescriptions.push_group_task} settingKey="push_group_task" value={settings.push_group_task} onChange={handleChange} />
          <SettingRow label="Obavijest članovima grupa zadatka" description={pushDescriptions.push_notify_groups_allowed} settingKey="push_notify_groups_allowed" value={settings.push_notify_groups_allowed} onChange={handleChange} />
          <SettingRow label="Obavijest dodijeljenima na zadatak" description={pushDescriptions.push_notify_assigned_allowed} settingKey="push_notify_assigned_allowed" value={settings.push_notify_assigned_allowed} onChange={handleChange} />
          <SettingRow label="Zadatak završen / novi ponavljajući" description={pushDescriptions.push_task_done} settingKey="push_task_done" value={settings.push_task_done} onChange={handleChange} />
          <SettingRow label="Zadatak ručno zatvoren" description={pushDescriptions.push_task_closed} settingKey="push_task_closed" value={settings.push_task_closed} onChange={handleChange} />
          <SettingRow label="Prijedlog zadatka odobren/odbijen" description={pushDescriptions.push_task_review} settingKey="push_task_review" value={settings.push_task_review} onChange={handleChange} />
          <SettingRow label="Zahtjev za odjavu prihvaćen/odbijen" description={pushDescriptions.push_removal_request} settingKey="push_removal_request" value={settings.push_removal_request} onChange={handleChange} />
          <SettingRow label="Nova prijava volontera za zadatak" description={pushDescriptions.push_volunteer_task} settingKey="push_volunteer_task" value={settings.push_volunteer_task} onChange={handleChange} />
          {settings.push_volunteer_task === 'true' && (
            <div style={{ padding: '4px 0 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 14, color: '#636e72' }}>Max 1 push po zadatku svakih</span>
              <input type="number" min="1" max="168" value={settings.volunteer_push_hours || '24'}
                onChange={e => handleNumberChange('volunteer_push_hours', e.target.value)} style={inputStyle} />
              <span style={{ fontSize: 14, color: '#636e72' }}>sati</span>
            </div>
          )}

          <SubHeader label="Intervencije" />
          <SettingRow label="Nova intervencija" description={pushDescriptions.push_new_intervention} settingKey="push_new_intervention" value={settings.push_new_intervention} onChange={handleChange} />
          <SettingRow label="Dodijeljen na intervenciju" description={pushDescriptions.push_intervention_participant} settingKey="push_intervention_participant" value={settings.push_intervention_participant} onChange={handleChange} />
          <SettingRow label="Nova prijava za intervenciju" description={pushDescriptions.push_volunteer_intervention} settingKey="push_volunteer_intervention" value={settings.push_volunteer_intervention} onChange={handleChange} />

          <SubHeader label="Ostalo" />
          <SettingRow label="Reset lozinke" description={pushDescriptions.push_password_reset} settingKey="push_password_reset" value={settings.push_password_reset} onChange={handleChange} />
        </div>
      </SectionCard>

      {/* Email notifikacije — sakriveno, vidi EMAIL_SECTION_ENABLED */}
      {EMAIL_SECTION_ENABLED && (
      <SectionCard title="Email notifikacije" icon={<Mail size={16} />}>
        <div style={{ paddingTop: 12 }}>
          <div style={{ background: '#fffbeb', border: '1px solid #fef3c7', borderLeft: '3px solid #f59e0b', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 14, color: '#856404', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            Email notifikacije zahtijevaju ispravno konfiguriran SMTP. Po defaultu su isključene.
          </div>
        </div>
        <SubHeader label="Zadaci" />
        <SettingRow label="Dodjela na zadatak" description={mailDescriptions.mail_new_task} settingKey="mail_new_task" value={settings.mail_new_task} onChange={handleChange} />
        <SettingRow label="Uklonjen sa zadatka" description={mailDescriptions.mail_task_removed} settingKey="mail_task_removed" value={settings.mail_task_removed} onChange={handleChange} />
        <SettingRow label="Novi grupni zadatak" description={mailDescriptions.mail_group_task} settingKey="mail_group_task" value={settings.mail_group_task} onChange={handleChange} />
        <SettingRow label="Zadatak završen / novi ponavljajući" description={mailDescriptions.mail_task_done} settingKey="mail_task_done" value={settings.mail_task_done} onChange={handleChange} />
        <SettingRow label="Zadatak ručno zatvoren" description={mailDescriptions.mail_task_closed} settingKey="mail_task_closed" value={settings.mail_task_closed} onChange={handleChange} />
        <SettingRow label="Prijedlog zadatka odobren/odbijen" description={mailDescriptions.mail_task_review} settingKey="mail_task_review" value={settings.mail_task_review} onChange={handleChange} />
        <SettingRow label="Zahtjev za odjavu prihvaćen/odbijen" description={mailDescriptions.mail_removal_request} settingKey="mail_removal_request" value={settings.mail_removal_request} onChange={handleChange} />
        <SettingRow label="Nova prijava volontera za zadatak" description={mailDescriptions.mail_volunteer_task} settingKey="mail_volunteer_task" value={settings.mail_volunteer_task} onChange={handleChange} />

        <SubHeader label="Intervencije" />
        <SettingRow label="Nova intervencija" description={mailDescriptions.mail_new_intervention} settingKey="mail_new_intervention" value={settings.mail_new_intervention} onChange={handleChange} />
        <SettingRow label="Dodijeljen na intervenciju" description={mailDescriptions.mail_intervention_participant} settingKey="mail_intervention_participant" value={settings.mail_intervention_participant} onChange={handleChange} />
        <SettingRow label="Nova prijava za intervenciju" description={mailDescriptions.mail_volunteer_intervention} settingKey="mail_volunteer_intervention" value={settings.mail_volunteer_intervention} onChange={handleChange} />

        <SubHeader label="Podsjetnici" />
        <SettingRow label="Podsjetnik X dana prije" description={mailDescriptions.mail_reminder_days} settingKey="mail_reminder_days" value={settings.mail_reminder_days} onChange={handleChange} />
        <SettingRow label="Podsjetnik X sati prije" description={mailDescriptions.mail_reminder_hours} settingKey="mail_reminder_hours" value={settings.mail_reminder_hours} onChange={handleChange} />

        <SubHeader label="Ostalo" />
        <SettingRow label="Reset lozinke" description={mailDescriptions.mail_password_reset} settingKey="mail_password_reset" value={settings.mail_password_reset} onChange={handleChange} />
        <div style={{ height: 8 }} />
      </SectionCard>
      )}

      <div style={{ height: 24 }} />
    </div>
  );
}

export default Settings;
